// client/src/routes/app/repairs/new.tsx — opening an incident · MAINT-007 (04 §8.9's LIST →
// FORM pair, 05 MAINT-007's „open-on-behalf flow").
//
// 04 §8.9 gives SCR-REP the two screens the module needs and no third one for the create, the
// way §8.4 does for contracts: a new ticket is the list's primary action and lands back on the
// detail it just made. That is what this route is — no new canonical screen, one form on its own
// URL so the browser's Back means „I changed my mind" instead of losing what was typed.
//
// `Operations.Write`, not `Operations.Read`: the `accountant` role reads the whole portfolio and
// writes none of it (BANK-004's D-725 found the same split for Banking), so a role that may see
// the board must not reach this URL.
//
// `?propertyId=` is where „Tichet nou" on a property's profile sends the form (D-2840): the
// apartment is already chosen, and „Renunțați" and the way back return to that profile's tab.

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { PageHeader } from '@/components/layout/PageHeader';
import { RequirePermission } from '@/components/layout/route-guards';
import { useRepairErrorMessage } from '@/features/repairs/errors';
import { useCreateRepairTicket } from '@/features/repairs/queries';
import { toTicketInput, type TicketFormValues } from '@/features/repairs/schema';
import { TicketForm } from '@/features/repairs/TicketForm';
import { toast } from '@/stores/toast-store';

const newTicketSearchSchema = z.object({
  propertyId: z.string().uuid().optional(),
});

export const Route = createFileRoute('/app/repairs/new')({
  validateSearch: newTicketSearchSchema,
  component: NewTicketScreen,
});

function NewTicketScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { propertyId } = Route.useSearch();
  const create = useCreateRepairTicket();
  const describe = useRepairErrorMessage();
  const [failure, setFailure] = useState<string | null>(null);

  const submit = async (values: TicketFormValues) => {
    setFailure(null);
    try {
      // A new incident has no attachments and no provider: the first is D-1113's seam, the
      // second is assigned by the deviz. Both are sent as what they are rather than omitted.
      const detail = await create.mutateAsync({ body: toTicketInput(values, [], null) });
      toast.success(t('repairs.form.created'));
      await navigate({ to: '/app/repairs/$ticketId', params: { ticketId: detail.id } });
    } catch (error) {
      setFailure(describe(error));
    }
  };

  return (
    <RequirePermission permission="Operations.Write">
      <PageHeader
        title={t('repairs.form.newTitle')}
        description={t('repairs.form.newSubtitle')}
        backTo={propertyId === undefined ? '/app/repairs' : '/app/properties/$propertyId/repairs'}
        backParams={propertyId === undefined ? undefined : { propertyId }}
        backLabel={
          propertyId === undefined ? t('repairs.detail.backToBoard') : t('properties.repairs.title')
        }
      />
      <TicketForm
        defaultPropertyId={propertyId}
        submitting={create.isPending}
        failure={failure}
        onSubmit={(values) => void submit(values)}
        onCancel={() => {
          if (propertyId === undefined) {
            void navigate({ to: '/app/repairs' });
          } else {
            void navigate({ to: '/app/properties/$propertyId/repairs', params: { propertyId } });
          }
        }}
      />
    </RequirePermission>
  );
}
